/**
 * Customer "Contacts & Routing" tab — CONTACTS-SUBCONTRACT-LAYER.md Prompt C,
 * built to the approved mockup mockups/contacts/contacts-admin-routing.html.
 *
 * The whole surface follows the approved exceptions-only doctrine:
 *
 *   · Hierarchy tree: account row → division rows (ONLY when the customer
 *     has divisions) → group rows (END CUSTOMER tag) → ungrouped row.
 *     Anything not set at a level inherits from the level above — inherited
 *     values NEVER render as repeated rows, just one quiet sentence.
 *     Green pill = "all N follow ✓"; amber pill = "N locations differ ▾"
 *     which expands to the exception sites ONLY.
 *   · People list: level badge (account / division / group / site),
 *     works-for badge (company name vs THEIR CUSTOMER), purposes, verified
 *     state. The add/edit drawer speaks plain language ("who gets X?") —
 *     never "create a routing rule" — and orchestrates the existing CRUD:
 *     contacts (crm.ts), scope (contactRoutingAdmin PATCH /contacts/:id/scope),
 *     rules + recipients (contactRoutingAdmin), with the billing-guardrail
 *     409 surfaced as a confirm dialog (confirm retries with override:true).
 *   · Coverage banner + auto-group assist (name-prefix clustering, office
 *     confirms batches → batch assign-sites endpoint). Snoozeable, and per
 *     the NON-BLOCKING doctrine nothing here is required by any other flow.
 *   · "How a send resolves" panel is driven by the REAL resolution service
 *     via GET /customers/:id/routing-resolution — never reimplemented here.
 */
import { useMemo, useState, type ReactNode } from "react";
import { UndeliverableBadge } from "@/components/undeliverable-badge";
import { BouncedSendsCard } from "@/components/bounced-sends-card";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
    AlertTriangle,
    Check,
    ChevronDown,
    ChevronRight,
    Loader2,
    Pencil,
    Plus,
    RotateCcw,
    Sparkles,
    Trash2,
    UserPlus,
    X,
} from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { confirmDialog } from "@/components/ui/app-dialogs";
import { isContactStale, formatVerifiedLabel } from "@/lib/contactFreshness";
import { buildRemovalMessage, removeContactsInBatches, summarizeRemoval } from "./contactRemoval";

// Re-exported so this tab's existing tests and importers keep one name for
// them; the implementations now live in contactRemoval.ts, shared with the
// plain Contacts tab (#1307).
export { summarizeRemoval, type BulkRemovalResponse, type ContactRemovalResult } from "./contactRemoval";

// ────────────────────────────────────────────────────────────
// Types (server shapes)
// ────────────────────────────────────────────────────────────

export interface RoutingSite {
    id: string;
    name: string | null;
    city: string | null;
    location_group_id: string | null;
}

export interface RoutingDivision {
    id: string;
    name: string;
    notes: string | null;
    group_count: number;
    contact_count: number;
}

export interface RoutingGroup {
    id: string;
    division_id: string | null;
    division_name: string | null;
    name: string;
    is_end_customer: boolean;
    site_count: number;
    contact_count: number;
}

export interface RuleRecipient {
    contact_method_id: string;
    is_cc: boolean;
    always_cc: boolean;
    kind: string;
    value: string;
    contact_id: string | null;
    contact_name: string | null;
    works_for: "account" | "end_customer" | null;
}

export interface RoutingRule {
    id: string;
    scope: "customer" | "division" | "group" | "site";
    division_id: string | null;
    location_group_id: string | null;
    site_id: string | null;
    purpose_key: string;
    recipients: RuleRecipient[];
}

/** Contact hierarchy read model (GET /customers/:id/contact-methods). */
export interface RoutingContactMethod {
    id: string;
    kind: string;
    value: string;
    isPrimary: boolean;
    purposes: string[];
    /** True when ServiceTitan owns this address (crm.ts emits `fromServiceTitan`). */
    fromServiceTitan?: boolean;
    /** Email only: SendGrid reported our last mail to it didn't arrive. */
    undeliverableAt?: string | null;
    undeliverableReason?: string | null;
}

export interface RoutingContact {
    id: string;
    firstName: string | null;
    lastName: string | null;
    roleTitle: string | null;
    siteId: string | null;
    divisionId: string | null;
    locationGroupId: string | null;
    worksFor: "account" | "end_customer";
    isPrimary: boolean;
    source: string;
    sourceContactId: string | null;
    lastVerifiedAt: string | null;
    lastVerifiedByName: string | null;
    methods: RoutingContactMethod[];
}

/** A removed imported record, restorable from the Contacts tab. */
export interface ImportSuppression {
    id: string;
    stContactId: string;
    kind: string | null;
    value: string | null;
    memo: string | null;
    contactName: string | null;
    suppressedAt: string;
    suppressedByName: string | null;
}

interface ResolutionRecipient {
    contact_method_id: string;
    contact_name: string | null;
    kind: string;
    value: string;
    is_cc: boolean;
    always_cc: boolean;
    works_for: string | null;
    resolved_from: { scope: string; rule_id: string };
}

interface ResolutionResponse {
    purposeKey: string;
    site: { id: string; name: string | null } | null;
    ladder: Array<{ scope: string; label: string }>;
    recipients: ResolutionRecipient[];
}

// ────────────────────────────────────────────────────────────
// Purpose vocabulary — plain office language, one place
// ────────────────────────────────────────────────────────────

export const PURPOSE_META: Record<string, { chip: string; question: string }> = {
    send_reports: { chip: "REPORTS", question: "Test reports & completed work orders" },
    send_invoices: { chip: "INVOICES", question: "Invoices" },
    send_quotes: { chip: "QUOTES", question: "Repair quotes (approval)" },
    send_appointment: { chip: "SCHEDULING", question: "Scheduling notices" },
    send_reminders: { chip: "REMINDERS", question: "Reminders" },
};

/** The purposes the coverage banner cares about (reminders is optional). */
export const CORE_PURPOSES = ["send_reports", "send_invoices", "send_quotes", "send_appointment"] as const;

export function purposeChipLabel(key: string): string {
    return PURPOSE_META[key]?.chip ?? key.replace(/^send_/, "").toUpperCase();
}

// ────────────────────────────────────────────────────────────
// Pure helpers (exported for tests)
// ────────────────────────────────────────────────────────────

/** Rules that actually route something (a rule with no recipients is inert). */
function liveRules(rules: RoutingRule[]): RoutingRule[] {
    return rules.filter((r) => r.recipients.length > 0);
}

export interface GroupNode {
    group: RoutingGroup;
    siteCount: number;
    rules: RoutingRule[];
    /** ONLY the sites that differ — inherited sites are never materialized. */
    exceptions: Array<{ site: RoutingSite; rules: RoutingRule[] }>;
}

export interface RoutingTree {
    accountRules: RoutingRule[];
    divisions: Array<{ division: RoutingDivision; rules: RoutingRule[]; groups: GroupNode[]; siteCount: number }>;
    /** Groups hanging directly off the customer (no division layer). */
    bareGroups: GroupNode[];
    ungrouped: { count: number; exceptions: Array<{ site: RoutingSite; rules: RoutingRule[] }> };
}

/**
 * Assemble the exceptions-only tree. A site is an "exception" iff it has at
 * least one site-scope rule with recipients; every other site is represented
 * only by its group's count. This is the anti-clutter contract from the
 * approved doctrine — inherited rows never exist in the output at all.
 */
export function buildRoutingTree(
    divisions: RoutingDivision[],
    groups: RoutingGroup[],
    sites: RoutingSite[],
    rules: RoutingRule[],
): RoutingTree {
    const live = liveRules(rules);
    const siteRulesBySite = new Map<string, RoutingRule[]>();
    for (const r of live) {
        if (r.scope === "site" && r.site_id) {
            const list = siteRulesBySite.get(r.site_id) ?? [];
            list.push(r);
            siteRulesBySite.set(r.site_id, list);
        }
    }
    const exceptionsFor = (groupId: string | null): Array<{ site: RoutingSite; rules: RoutingRule[] }> =>
        sites
            .filter((s) => s.location_group_id === groupId && siteRulesBySite.has(s.id))
            .map((s) => ({ site: s, rules: siteRulesBySite.get(s.id)! }));

    const toNode = (g: RoutingGroup): GroupNode => ({
        group: g,
        siteCount: sites.filter((s) => s.location_group_id === g.id).length,
        rules: live.filter((r) => r.scope === "group" && r.location_group_id === g.id),
        exceptions: exceptionsFor(g.id),
    });

    const divisionNodes = divisions.map((d) => {
        const dGroups = groups.filter((g) => g.division_id === d.id).map(toNode);
        return {
            division: d,
            rules: live.filter((r) => r.scope === "division" && r.division_id === d.id),
            groups: dGroups,
            siteCount: dGroups.reduce((n, g) => n + g.siteCount, 0),
        };
    });

    return {
        accountRules: live.filter((r) => r.scope === "customer"),
        divisions: divisionNodes,
        bareGroups: groups.filter((g) => g.division_id === null).map(toNode),
        ungrouped: {
            count: sites.filter((s) => s.location_group_id === null).length,
            exceptions: exceptionsFor(null),
        },
    };
}

export interface GroupSuggestion {
    /** Suggested group name (the shared prefix). */
    name: string;
    /** Existing group with the same name, when one already exists. */
    existingGroupId: string | null;
    siteIds: string[];
}

/** "7-Eleven #36114" → "7-Eleven"; "AutoZone 1188" → "AutoZone". */
export function sitePrefix(name: string): string | null {
    let prefix = name;
    const hash = name.indexOf("#");
    if (hash >= 0) prefix = name.slice(0, hash);
    else prefix = name.replace(/[\s\-–—·.,]*\d+\s*$/, "");
    prefix = prefix.replace(/[\s\-–—·.,]+$/, "").trim();
    if (prefix.length < 3) return null;
    if (prefix === name.trim()) return null; // nothing was stripped — not a numbered-site pattern
    return prefix;
}

/**
 * Auto-group assist: cluster UNGROUPED sites by name prefix. Suggestions
 * with ≥ 3 sites only; a prefix matching an existing group name suggests
 * assignment to that group instead of creating a duplicate.
 */
export function suggestGroups(sites: RoutingSite[], groups: RoutingGroup[]): GroupSuggestion[] {
    const clusters = new Map<string, { name: string; siteIds: string[] }>();
    for (const s of sites) {
        if (s.location_group_id !== null || !s.name) continue;
        const prefix = sitePrefix(s.name);
        if (!prefix) continue;
        const key = prefix.toLowerCase();
        const c = clusters.get(key) ?? { name: prefix, siteIds: [] };
        c.siteIds.push(s.id);
        clusters.set(key, c);
    }
    const byName = new Map(groups.map((g) => [g.name.toLowerCase(), g.id]));
    return Array.from(clusters.values())
        .filter((c) => c.siteIds.length >= 3 || byName.has(c.name.toLowerCase()))
        .sort((a, b) => b.siteIds.length - a.siteIds.length)
        .map((c) => ({ name: c.name, existingGroupId: byName.get(c.name.toLowerCase()) ?? null, siteIds: c.siteIds }));
}

export interface CoverageSummary {
    /** Core purposes with NO account-default rule (informational — nothing blocks). */
    uncoveredPurposes: string[];
    siteExceptionCount: number;
    staleContactCount: number;
}

export function coverageSummary(rules: RoutingRule[], contacts: RoutingContact[]): CoverageSummary {
    const live = liveRules(rules);
    const covered = new Set(live.filter((r) => r.scope === "customer").map((r) => r.purpose_key));
    const exceptionSites = new Set(live.filter((r) => r.scope === "site" && r.site_id).map((r) => r.site_id));
    return {
        uncoveredPurposes: CORE_PURPOSES.filter((p) => !covered.has(p)),
        siteExceptionCount: exceptionSites.size,
        staleContactCount: contacts.filter((c) => isContactStale(c.lastVerifiedAt)).length,
    };
}

/** Where a contact sits on the ladder. */
export type ContactLevel =
    | { kind: "account" }
    | { kind: "division"; id: string }
    | { kind: "group"; id: string }
    | { kind: "site"; id: string };

export function contactLevel(c: Pick<RoutingContact, "siteId" | "divisionId" | "locationGroupId">): ContactLevel {
    if (c.siteId) return { kind: "site", id: c.siteId };
    if (c.locationGroupId) return { kind: "group", id: c.locationGroupId };
    if (c.divisionId) return { kind: "division", id: c.divisionId };
    return { kind: "account" };
}

// ────────────────────────────────────────────────────────────
// API orchestration helpers (exported for tests)
// ────────────────────────────────────────────────────────────

/**
 * Find-or-create the routing rule for (level, purpose). Existing rules are
 * matched from the already-loaded list; a create that loses a race (409
 * DUPLICATE_RULE) re-reads the list once. Never called "creating a rule" in
 * the UI — the office just answered "who gets X?".
 */
export async function ensureRuleId(args: {
    customerId: string;
    level: ContactLevel;
    purposeKey: string;
    rules: RoutingRule[];
}): Promise<string> {
    const { customerId, level, purposeKey } = args;
    const matches = (r: RoutingRule): boolean => {
        if (r.purpose_key !== purposeKey) return false;
        switch (level.kind) {
            case "account":
                return r.scope === "customer";
            case "division":
                return r.scope === "division" && r.division_id === level.id;
            case "group":
                return r.scope === "group" && r.location_group_id === level.id;
            case "site":
                return r.scope === "site" && r.site_id === level.id;
        }
    };
    const existing = args.rules.find(matches);
    if (existing) return existing.id;

    const body: Record<string, unknown> = {
        scope: level.kind === "account" ? "customer" : level.kind,
        purposeKey,
    };
    if (level.kind === "division") body.divisionId = level.id;
    if (level.kind === "group") body.locationGroupId = level.id;
    if (level.kind === "site") body.siteId = level.id;
    try {
        const res = await api.post<{ routingRule: { id: string } }>(`/api/customers/${customerId}/routing-rules`, body);
        return res.routingRule.id;
    } catch (e) {
        if (e instanceof ApiError && e.status === 409) {
            const fresh = await api.get<{ routingRules: RoutingRule[] }>(`/api/customers/${customerId}/routing-rules`);
            const found = fresh.routingRules.find(matches);
            if (found) return found.id;
        }
        throw e;
    }
}

/**
 * Attach a recipient to a rule, surfacing the end-customer billing guardrail
 * (409 END_CUSTOMER_BILLING_OVERRIDE_REQUIRED) as a confirm dialog; confirming
 * retries with override:true (audited server-side). Returns "declined" when
 * the office backs out — callers just skip that purpose, nothing blocks.
 */
export async function attachRecipientGuarded(args: {
    ruleId: string;
    contactMethodId: string;
    isCc?: boolean;
    alwaysCc?: boolean;
    /** Injectable for tests; defaults to the app-styled confirm dialog. */
    confirm?: (message: string) => Promise<boolean>;
}): Promise<"attached" | "declined"> {
    const confirm =
        args.confirm ??
        ((message: string) =>
            confirmDialog({
                title: "Billing guardrail",
                message,
                confirmLabel: "Bill them anyway",
                destructive: true,
            }));
    const body = {
        contactMethodId: args.contactMethodId,
        isCc: args.isCc ?? false,
        alwaysCc: args.alwaysCc ?? false,
    };
    try {
        await api.post(`/api/routing-rules/${args.ruleId}/recipients`, body);
        return "attached";
    } catch (e) {
        if (
            e instanceof ApiError &&
            e.status === 409 &&
            (e.body as { error?: string } | null)?.error === "END_CUSTOMER_BILLING_OVERRIDE_REQUIRED"
        ) {
            const message =
                (e.body as { message?: string }).message ??
                "This person works for the end customer — bill them anyway?";
            if (!(await confirm(message))) return "declined";
            await api.post(`/api/routing-rules/${args.ruleId}/recipients`, { ...body, override: true });
            return "attached";
        }
        throw e;
    }
}

// ────────────────────────────────────────────────────────────
// Small presentational atoms
// ────────────────────────────────────────────────────────────

/** Purpose→recipient chip ("REPORTS facilities-tx@7-11.com"). */
function RuleChips({
    rules,
    onRemoveRecipient,
}: {
    rules: RoutingRule[];
    onRemoveRecipient?: (ruleId: string, contactMethodId: string, value: string) => void;
}) {
    return (
        <>
            {rules.map((rule) =>
                rule.recipients.map((rec) => (
                    <span
                        key={`${rule.id}:${rec.contact_method_id}`}
                        className="inline-flex items-center gap-1.5 rounded-full border border-amber-300/70 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200"
                        data-testid="rule-chip"
                    >
                        <span className="text-[9px] font-extrabold tracking-wider text-amber-700 dark:text-amber-300">
                            {rec.always_cc
                                ? "ALWAYS CC"
                                : rec.is_cc
                                  ? `${purposeChipLabel(rule.purpose_key)} CC`
                                  : purposeChipLabel(rule.purpose_key)}
                        </span>
                        <span className="max-w-[220px] truncate">{rec.value}</span>
                        {onRemoveRecipient && (
                            <button
                                type="button"
                                className="text-amber-700/60 hover:text-red-600 dark:text-amber-300/60"
                                aria-label={`Stop sending ${purposeChipLabel(rule.purpose_key).toLowerCase()} to ${rec.value}`}
                                onClick={() => onRemoveRecipient(rule.id, rec.contact_method_id, rec.value)}
                            >
                                <X className="h-3 w-3" />
                            </button>
                        )}
                    </span>
                )),
            )}
        </>
    );
}

function GreenPill({ children }: { children: ReactNode }) {
    return (
        <span
            data-testid="pill-green"
            className="inline-flex items-center rounded-full bg-emerald-50 px-2.5 py-0.5 text-[10.5px] font-bold text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300"
        >
            {children}
        </span>
    );
}

function AmberPill({ children, onClick, expanded }: { children: ReactNode; onClick: () => void; expanded: boolean }) {
    return (
        <button
            type="button"
            data-testid="pill-amber"
            aria-expanded={expanded}
            onClick={onClick}
            className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-0.5 text-[10.5px] font-bold text-amber-800 hover:bg-amber-200 dark:bg-amber-500/15 dark:text-amber-300 dark:hover:bg-amber-500/25"
        >
            {children}
            {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        </button>
    );
}

function InheritNote({ children }: { children: ReactNode }) {
    return <span className="text-[11.5px] italic text-foreground/60">{children}</span>;
}

function EndCustomerTag() {
    return (
        <span className="rounded border border-orange-300 bg-orange-50 px-1.5 py-px text-[9px] font-extrabold tracking-wider text-orange-700 dark:border-orange-500/40 dark:bg-orange-500/10 dark:text-orange-300">
            THEIR CUSTOMER
        </span>
    );
}

/** works_for badge: the paying account's name vs THEIR CUSTOMER. */
function WorksForBadge({ worksFor, customerName }: { worksFor: string; customerName: string }) {
    if (worksFor === "end_customer") {
        return (
            <span className="rounded border border-orange-300 bg-orange-50 px-1.5 py-px text-[9px] font-extrabold tracking-wider text-orange-700 dark:border-orange-500/40 dark:bg-orange-500/10 dark:text-orange-300">
                THEIR CUSTOMER
            </span>
        );
    }
    return (
        <span className="max-w-[160px] truncate rounded border border-blue-300 bg-blue-50 px-1.5 py-px text-[9px] font-extrabold tracking-wider text-blue-700 dark:border-blue-500/40 dark:bg-blue-500/10 dark:text-blue-300">
            {customerName.toUpperCase()}
        </span>
    );
}

function LevelBadge({ label }: { label: string }) {
    return (
        <span className="max-w-[220px] truncate rounded bg-muted px-1.5 py-px text-[10px] font-bold text-foreground/70">
            {label}
        </span>
    );
}

// ────────────────────────────────────────────────────────────
// Hierarchy tree (exceptions-only rendering)
// ────────────────────────────────────────────────────────────

function ExceptionRows({
    exceptions,
    indentClass,
}: {
    exceptions: Array<{ site: RoutingSite; rules: RoutingRule[] }>;
    indentClass: string;
}) {
    return (
        <div
            className={cn(
                "border-t border-dashed border-amber-200 bg-amber-50/60 py-2 dark:bg-amber-500/5",
                indentClass,
            )}
        >
            {exceptions.map(({ site, rules }) => (
                <div
                    key={site.id}
                    data-testid="exception-row"
                    className="flex flex-wrap items-center gap-2 py-1 text-xs"
                >
                    <span className="w-56 shrink-0 truncate font-semibold">{site.name ?? site.id.slice(0, 8)}</span>
                    <span className="flex flex-wrap items-center gap-1.5 text-foreground/70">
                        <RuleChips rules={rules} />
                    </span>
                </div>
            ))}
        </div>
    );
}

function GroupRow({
    node,
    parentLabel,
    customerName,
    indent,
    onRemoveRecipient,
}: {
    node: GroupNode;
    parentLabel: string;
    customerName: string;
    indent: 1 | 2;
    onRemoveRecipient?: (ruleId: string, contactMethodId: string, value: string) => void;
}) {
    const [expanded, setExpanded] = useState(false);
    const differ = node.exceptions.length;
    const indentClass = indent === 2 ? "pl-16 pr-4" : "pl-10 pr-4";
    return (
        <div data-testid={`group-row-${node.group.id}`}>
            <div className={cn("flex flex-wrap items-center gap-2 border-b border-border/60 py-3", indentClass)}>
                <span className="flex items-center gap-2 text-[13.5px] font-bold">
                    {node.group.name}
                    {node.group.is_end_customer && <EndCustomerTag />}
                    <span className="text-[11px] font-medium text-foreground/60">
                        {node.siteCount} site{node.siteCount === 1 ? "" : "s"}
                    </span>
                </span>
                <span className="ml-auto flex flex-wrap items-center justify-end gap-1.5">
                    {node.rules.length > 0 ? (
                        <RuleChips rules={node.rules} onRemoveRecipient={onRemoveRecipient} />
                    ) : (
                        <InheritNote>no brand people yet — follows {parentLabel} ↑</InheritNote>
                    )}
                    {differ > 0 ? (
                        <AmberPill onClick={() => setExpanded((v) => !v)} expanded={expanded}>
                            {differ} location{differ === 1 ? "" : "s"} differ{differ === 1 ? "s" : ""}
                        </AmberPill>
                    ) : node.rules.length > 0 ? (
                        <GreenPill>all {node.siteCount} follow ✓</GreenPill>
                    ) : (
                        <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-[10.5px] font-bold text-foreground/60">
                            ask {customerName.split(" ")[0]}
                        </span>
                    )}
                </span>
            </div>
            {expanded && differ > 0 && (
                <ExceptionRows exceptions={node.exceptions} indentClass={indent === 2 ? "pl-24 pr-4" : "pl-16 pr-4"} />
            )}
        </div>
    );
}

export function HierarchyTree({
    customerName,
    divisions,
    groups,
    sites,
    rules,
    onRemoveRecipient,
    onAddDivision,
    onAddGroup,
}: {
    customerName: string;
    divisions: RoutingDivision[];
    groups: RoutingGroup[];
    sites: RoutingSite[];
    rules: RoutingRule[];
    onRemoveRecipient?: (ruleId: string, contactMethodId: string, value: string) => void;
    onAddDivision?: () => void;
    onAddGroup?: () => void;
}) {
    const tree = useMemo(() => buildRoutingTree(divisions, groups, sites, rules), [divisions, groups, sites, rules]);
    const [ungroupedExpanded, setUngroupedExpanded] = useState(false);
    const hasDivisions = tree.divisions.length > 0;
    const groupIndent: 1 | 2 = hasDivisions ? 2 : 1;
    const totalSites = sites.length;
    const ungroupedDiffer = tree.ungrouped.exceptions.length;

    return (
        <Card>
            <CardContent className="p-0">
                {/* Account row */}
                <div className="flex flex-wrap items-center gap-2 border-b bg-muted/40 px-4 py-3">
                    <span className="flex items-baseline gap-2 text-[13.5px] font-bold">
                        {customerName}
                        <span className="text-[11px] font-medium text-foreground/60">
                            account defaults — apply to all {totalSites} site{totalSites === 1 ? "" : "s"} unless
                            overridden below
                        </span>
                    </span>
                    <span className="ml-auto flex flex-wrap items-center justify-end gap-1.5">
                        {tree.accountRules.length > 0 ? (
                            <RuleChips rules={tree.accountRules} onRemoveRecipient={onRemoveRecipient} />
                        ) : (
                            <InheritNote>no account defaults yet — sends ask the office at send time</InheritNote>
                        )}
                    </span>
                </div>

                {/* Division rows — ONLY when the customer has divisions */}
                {tree.divisions.map(({ division, rules: dRules, groups: dGroups, siteCount }) => (
                    <div key={division.id} data-testid={`division-row-${division.id}`}>
                        <div className="flex flex-wrap items-center gap-2 border-b border-border/60 bg-muted/20 py-3 pl-10 pr-4">
                            <span className="flex items-baseline gap-2 text-[13.5px] font-bold">
                                {division.name}
                                <span className="text-[11px] font-medium text-foreground/60">
                                    division · {dGroups.length} brand{dGroups.length === 1 ? "" : "s"} · {siteCount}{" "}
                                    sites
                                </span>
                            </span>
                            <span className="ml-auto flex flex-wrap items-center justify-end gap-1.5">
                                {dRules.length > 0 ? (
                                    <>
                                        <RuleChips rules={dRules} onRemoveRecipient={onRemoveRecipient} />
                                        <InheritNote>everything else follows account ↑</InheritNote>
                                    </>
                                ) : (
                                    <InheritNote>everything follows account ↑</InheritNote>
                                )}
                            </span>
                        </div>
                        {dGroups.map((node) => (
                            <GroupRow
                                key={node.group.id}
                                node={node}
                                parentLabel="division"
                                customerName={customerName}
                                indent={2}
                                onRemoveRecipient={onRemoveRecipient}
                            />
                        ))}
                    </div>
                ))}

                {/* Groups without a division (flat tree for plain customers) */}
                {tree.bareGroups.map((node) => (
                    <GroupRow
                        key={node.group.id}
                        node={node}
                        parentLabel="account"
                        customerName={customerName}
                        indent={groupIndent}
                        onRemoveRecipient={onRemoveRecipient}
                    />
                ))}

                {/* Ungrouped sites — one quiet row, exceptions only */}
                <div
                    className={cn(
                        "flex flex-wrap items-center gap-2 py-3 pr-4",
                        hasDivisions || tree.bareGroups.length > 0 ? "pl-10" : "pl-4",
                    )}
                >
                    <span className="text-[13px] font-medium text-foreground/70">
                        Ungrouped sites <span className="text-[11px] text-foreground/60">{tree.ungrouped.count}</span>
                    </span>
                    <span className="ml-auto flex flex-wrap items-center justify-end gap-1.5">
                        {ungroupedDiffer > 0 ? (
                            <AmberPill onClick={() => setUngroupedExpanded((v) => !v)} expanded={ungroupedExpanded}>
                                {ungroupedDiffer} differ
                            </AmberPill>
                        ) : (
                            <InheritNote>follow account defaults ↑</InheritNote>
                        )}
                    </span>
                </div>
                {ungroupedExpanded && ungroupedDiffer > 0 && (
                    <ExceptionRows exceptions={tree.ungrouped.exceptions} indentClass="pl-16 pr-4" />
                )}

                {/* Low-key structure affordances */}
                {(onAddDivision || onAddGroup) && (
                    <div className="flex items-center gap-4 border-t px-4 py-2">
                        {onAddGroup && (
                            <button
                                type="button"
                                onClick={onAddGroup}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                            >
                                <Plus className="h-3 w-3" /> Add group
                            </button>
                        )}
                        {onAddDivision && (
                            <button
                                type="button"
                                onClick={onAddDivision}
                                className="inline-flex items-center gap-1 text-xs font-semibold text-foreground/60 hover:text-foreground hover:underline"
                            >
                                <Plus className="h-3 w-3" /> Add division
                            </button>
                        )}
                        <span className="ml-auto text-[11px] text-muted-foreground">
                            Anything not set here follows the level above it. Only exceptions are ever shown.
                        </span>
                    </div>
                )}
            </CardContent>
        </Card>
    );
}

// ────────────────────────────────────────────────────────────
// Auto-group assist
// ────────────────────────────────────────────────────────────

/**
 * Batch-confirm dialog: the office reviews each suggested cluster and applies
 * the checked ones. Applying creates the group when it doesn't exist yet,
 * then calls the batch assign-sites endpoint with the confirmed site ids.
 */
export function AutoGroupSuggestionsDialog({
    customerId,
    suggestions,
    onClose,
    onApplied,
}: {
    customerId: string;
    suggestions: GroupSuggestion[];
    onClose: () => void;
    onApplied?: () => void;
}) {
    const [checked, setChecked] = useState<Record<string, boolean>>(() =>
        Object.fromEntries(suggestions.map((s) => [s.name, true])),
    );
    const apply = useMutation({
        mutationFn: async () => {
            let applied = 0;
            for (const s of suggestions) {
                if (!checked[s.name]) continue;
                let groupId = s.existingGroupId;
                if (!groupId) {
                    const res = await api.post<{ locationGroup: { id: string } }>(
                        `/api/customers/${customerId}/location-groups`,
                        { name: s.name },
                    );
                    groupId = res.locationGroup.id;
                }
                await api.post(`/api/location-groups/${groupId}/assign-sites`, { siteIds: s.siteIds });
                applied += s.siteIds.length;
            }
            return applied;
        },
        onSuccess: (applied) => {
            toast.success(`Grouped ${applied} site${applied === 1 ? "" : "s"}`);
            onApplied?.();
            onClose();
        },
        onError: () => toast.error("Couldn't apply the grouping — nothing else was changed"),
    });

    return (
        <Dialog open onClose={onClose} title="Group sites by name">
            <div className="space-y-3">
                <p className="text-sm text-foreground/80">
                    These ungrouped locations share a name pattern. Check the batches that look right — nothing is
                    applied until you confirm.
                </p>
                {suggestions.map((s) => (
                    <label key={s.name} className="flex items-center gap-2 rounded-lg border p-2.5 text-sm">
                        <Checkbox
                            checked={checked[s.name] ?? false}
                            onCheckedChange={(v) => setChecked((c) => ({ ...c, [s.name]: v === true }))}
                            aria-label={`Group ${s.siteIds.length} sites as ${s.name}`}
                        />
                        <span className="font-semibold">{s.name}</span>
                        <span className="text-foreground/70">
                            ×{s.siteIds.length}
                            {s.existingGroupId ? " → existing group" : " → new group"}
                        </span>
                    </label>
                ))}
                <div className="flex justify-end gap-2 pt-1">
                    <Button variant="outline" size="sm" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        size="sm"
                        disabled={apply.isPending || suggestions.every((s) => !checked[s.name])}
                        onClick={() => apply.mutate()}
                    >
                        {apply.isPending ? (
                            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                        ) : (
                            <Check className="mr-1.5 h-3.5 w-3.5" />
                        )}
                        Apply checked batches
                    </Button>
                </div>
            </div>
        </Dialog>
    );
}

const snoozeKey = (customerId: string) => `gwc.routingAssistSnooze.${customerId}`;

function assistSnoozed(customerId: string): boolean {
    try {
        const until = window.localStorage.getItem(snoozeKey(customerId));
        return until !== null && Number(until) > Date.now();
    } catch {
        return false;
    }
}

function snoozeAssist(customerId: string): void {
    try {
        window.localStorage.setItem(snoozeKey(customerId), String(Date.now() + 7 * 24 * 60 * 60 * 1000));
    } catch {
        /* private mode — snooze just won't persist */
    }
}

// ────────────────────────────────────────────────────────────
// Structure dialogs (add division / add group)
// ────────────────────────────────────────────────────────────

function AddDivisionDialog({ customerId, onClose }: { customerId: string; onClose: () => void }) {
    const qc = useQueryClient();
    const [name, setName] = useState("");
    const save = useMutation({
        mutationFn: () => api.post(`/api/customers/${customerId}/divisions`, { name: name.trim() }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["customer", customerId, "routing"] });
            toast.success("Division added");
            onClose();
        },
        onError: (e) =>
            toast.error(
                e instanceof ApiError && (e.body as { error?: string })?.error === "DUPLICATE_NAME"
                    ? "A division with that name already exists"
                    : "Couldn't add the division",
            ),
    });
    return (
        <Dialog open onClose={onClose} title="Add division">
            <form
                className="space-y-3"
                onSubmit={(e) => {
                    e.preventDefault();
                    if (name.trim()) save.mutate();
                }}
            >
                <p className="text-sm text-foreground/80">
                    Divisions are for accounts like Cintas where separate service centers each manage their own brands.
                    Most customers never need one.
                </p>
                <div>
                    <label className="text-xs font-medium">Division name</label>
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        autoFocus
                        placeholder="e.g. Cintas Fire — South TX Service Center"
                    />
                </div>
                <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="submit" size="sm" disabled={save.isPending || !name.trim()}>
                        {save.isPending ? "Adding…" : "Add division"}
                    </Button>
                </div>
            </form>
        </Dialog>
    );
}

function AddGroupDialog({
    customerId,
    divisions,
    onClose,
}: {
    customerId: string;
    divisions: RoutingDivision[];
    onClose: () => void;
}) {
    const qc = useQueryClient();
    const [name, setName] = useState("");
    const [divisionId, setDivisionId] = useState("");
    const [isEndCustomer, setIsEndCustomer] = useState(false);
    const save = useMutation({
        mutationFn: () =>
            api.post(`/api/customers/${customerId}/location-groups`, {
                name: name.trim(),
                divisionId: divisionId || null,
                isEndCustomer,
            }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["customer", customerId, "routing"] });
            toast.success("Group added");
            onClose();
        },
        onError: (e) =>
            toast.error(
                e instanceof ApiError && (e.body as { error?: string })?.error === "DUPLICATE_NAME"
                    ? "A group with that name already exists"
                    : "Couldn't add the group",
            ),
    });
    return (
        <Dialog open onClose={onClose} title="Add group">
            <form
                className="space-y-3"
                onSubmit={(e) => {
                    e.preventDefault();
                    if (name.trim()) save.mutate();
                }}
            >
                <div>
                    <label className="text-xs font-medium">Group name</label>
                    <Input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        autoFocus
                        placeholder="e.g. 7-Eleven"
                    />
                </div>
                {divisions.length > 0 && (
                    <div>
                        <label className="text-xs font-medium">Managed by division (optional)</label>
                        <Select value={divisionId} onChange={(e) => setDivisionId(e.target.value)}>
                            <option value="">— none, hangs off the account —</option>
                            {divisions.map((d) => (
                                <option key={d.id} value={d.id}>
                                    {d.name}
                                </option>
                            ))}
                        </Select>
                    </div>
                )}
                <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={isEndCustomer} onCheckedChange={(v) => setIsEndCustomer(v === true)} />
                    This is <b>their customer</b> (a brand they service — its people never get invoices)
                </label>
                <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" size="sm" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button type="submit" size="sm" disabled={save.isPending || !name.trim()}>
                        {save.isPending ? "Adding…" : "Add group"}
                    </Button>
                </div>
            </form>
        </Dialog>
    );
}

// ────────────────────────────────────────────────────────────
// Person drawer (add / edit) — plain language, guardrail-aware
// ────────────────────────────────────────────────────────────

interface PersonDrawerProps {
    customerId: string;
    customerName: string;
    divisions: RoutingDivision[];
    groups: RoutingGroup[];
    sites: RoutingSite[];
    rules: RoutingRule[];
    /** Editing an existing person; undefined = adding a new one. */
    contact?: RoutingContact;
    onClose: () => void;
}

/** Purposes a contact's methods are attached to (across all their methods). */
function attachedPurposes(contact: RoutingContact, rules: RoutingRule[]): Set<string> {
    const methodIds = new Set(contact.methods.map((m) => m.id));
    const out = new Set<string>();
    for (const r of rules) {
        if (r.recipients.some((rec) => methodIds.has(rec.contact_method_id))) out.add(r.purpose_key);
    }
    return out;
}

/**
 * Every email and phone on one contact, with where each came from (#1141).
 *
 * The drawer used to show a single Email box seeded from the first method and
 * save it through the legacy `customer_contacts` scalar. On a ServiceTitan
 * contact that box was disabled outright, and unlocking it would not have been
 * enough: the legacy mirror only writes rows where `st_contact_id IS NULL`, so
 * a save INSERTed a second address instead of changing the visible one. The
 * new address sorted first (it is written primary; ST rows are not), so the
 * screen showed the edit while the imported address stayed live and kept
 * receiving. Listing every address makes what is on file match what is on
 * screen, and gives add/remove somewhere honest to live.
 *
 * Adds and removes apply immediately — like the routing checkboxes below, and
 * unlike the name/role fields, which still save with the form.
 */
function ContactAddressList({
    customerId,
    contactId,
    methods,
}: {
    customerId: string;
    contactId: string;
    methods: RoutingContactMethod[];
}) {
    const qc = useQueryClient();
    const [adding, setAdding] = useState<"email" | "phone" | null>(null);
    const [draft, setDraft] = useState("");

    const refresh = () => {
        qc.invalidateQueries({ queryKey: ["customer", customerId, "routing"] });
        qc.invalidateQueries({ queryKey: ["customer", customerId, "contacts"] });
    };

    const addMethod = useMutation({
        mutationFn: (body: { kind: "email" | "phone"; value: string }) =>
            api.post(`/api/customers/${customerId}/contacts/${contactId}/methods`, body),
        onSuccess: (_d, body) => {
            refresh();
            setAdding(null);
            setDraft("");
            toast.success(body.kind === "email" ? "Email address added" : "Phone number added");
        },
        onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't add that address"),
    });

    const removeMethod = useMutation({
        mutationFn: (methodId: string) =>
            api.delete(`/api/customers/${customerId}/contacts/${contactId}/methods/${methodId}`),
        onSuccess: () => {
            refresh();
            toast.success("Address removed");
        },
        onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't remove that address"),
    });

    async function confirmRemove(m: RoutingContactMethod) {
        // Routing attachments cascade off the delete, so say what stops — and
        // for an imported address, say that it stays gone (#1303). That used
        // to be a 409: the import would have restored it on the next sync.
        const lines = [
            m.purposes.length > 0
                ? "This address is set to receive documents. Removing it stops those sends to this address."
                : "This address will be removed from this contact.",
        ];
        if (m.fromServiceTitan) {
            lines.push(
                "It came from the job system and will not come back on the next sync — restorable from this tab.",
            );
        }
        const ok = await confirmDialog({
            title: `Remove ${m.value}?`,
            message: lines.join(" "),
            confirmLabel: "Remove",
            destructive: true,
            focusCancel: true,
        });
        if (ok) removeMethod.mutate(m.id);
    }

    const emails = methods.filter((m) => m.kind === "email");
    const phones = methods.filter((m) => m.kind === "phone");

    const row = (m: RoutingContactMethod) => (
        <div key={m.id} className="flex items-center gap-2 rounded-md border bg-card px-2.5 py-1.5">
            <span className="min-w-0 flex-1 truncate text-sm">{m.value}</span>
            {m.undeliverableAt ? <UndeliverableBadge at={m.undeliverableAt} reason={m.undeliverableReason} /> : null}
            {m.fromServiceTitan ? (
                <span className="shrink-0 rounded border px-1.5 py-0.5 text-[10px] uppercase tracking-wide text-foreground/55">
                    Imported
                </span>
            ) : null}
            <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 shrink-0 px-2 text-xs"
                disabled={removeMethod.isPending}
                title={
                    m.fromServiceTitan
                        ? "Remove this imported address — it stays removed, and the sync won't put it back"
                        : "Remove this address"
                }
                onClick={() => void confirmRemove(m)}
            >
                <X className="h-3.5 w-3.5" />
            </Button>
        </div>
    );

    const addRow = (kind: "email" | "phone") =>
        adding === kind ? (
            <div className="flex items-center gap-2">
                <Input
                    autoFocus
                    value={draft}
                    placeholder={kind === "email" ? "name@company.com" : "(555) 555-0100"}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter") {
                            e.preventDefault();
                            if (draft.trim()) addMethod.mutate({ kind, value: draft.trim() });
                        }
                        if (e.key === "Escape") {
                            setAdding(null);
                            setDraft("");
                        }
                    }}
                />
                <Button
                    type="button"
                    size="sm"
                    disabled={!draft.trim() || addMethod.isPending}
                    onClick={() => addMethod.mutate({ kind, value: draft.trim() })}
                >
                    {addMethod.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Add"}
                </Button>
                <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                        setAdding(null);
                        setDraft("");
                    }}
                >
                    Cancel
                </Button>
            </div>
        ) : (
            <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-foreground/70"
                onClick={() => {
                    setAdding(kind);
                    setDraft("");
                }}
            >
                <Plus className="mr-1 h-3.5 w-3.5" />
                {kind === "email" ? "Add an email address" : "Add a phone number"}
            </Button>
        );

    return (
        <div className="space-y-3">
            <div className="space-y-1.5">
                <label className="text-xs font-medium">Email addresses</label>
                {emails.length === 0 && adding !== "email" ? (
                    <p className="text-[11px] text-foreground/55">No email address on file.</p>
                ) : (
                    emails.map(row)
                )}
                {addRow("email")}
            </div>
            <div className="space-y-1.5">
                <label className="text-xs font-medium">Phone numbers</label>
                {phones.length === 0 && adding !== "phone" ? (
                    <p className="text-[11px] text-foreground/55">No phone number on file.</p>
                ) : (
                    phones.map(row)
                )}
                {addRow("phone")}
            </div>
        </div>
    );
}

export function PersonDrawer({
    customerId,
    customerName,
    divisions,
    groups,
    sites,
    rules,
    contact,
    onClose,
}: PersonDrawerProps) {
    const qc = useQueryClient();
    const isEdit = !!contact;
    const initialLevel = contact ? contactLevel(contact) : ({ kind: "account" } as ContactLevel);

    const [firstName, setFirstName] = useState(contact?.firstName ?? "");
    const [lastName, setLastName] = useState(contact?.lastName ?? "");
    const [roleTitle, setRoleTitle] = useState(contact?.roleTitle ?? "");
    const [email, setEmail] = useState(contact?.methods.find((m) => m.kind === "email")?.value ?? "");
    const [phone, setPhone] = useState(contact?.methods.find((m) => m.kind === "phone")?.value ?? "");
    const [levelKind, setLevelKind] = useState<ContactLevel["kind"]>(initialLevel.kind);
    const [levelId, setLevelId] = useState<string>("id" in initialLevel ? initialLevel.id : "");
    const [worksFor, setWorksFor] = useState<"account" | "end_customer">(contact?.worksFor ?? "account");
    const [siteFilter, setSiteFilter] = useState("");
    const [purposes, setPurposes] = useState<Set<string>>(() =>
        contact ? attachedPurposes(contact, rules) : new Set(),
    );
    const initialPurposes = useMemo(
        () => (contact ? attachedPurposes(contact, rules) : new Set<string>()),
        [contact, rules],
    );

    // Identity fields are editable only through the legacy CRUD mirror; a
    // pure ST-import (no legacy anchor) keeps its imported identity.
    const identityLocked = isEdit && !contact?.sourceContactId;

    const currentLevel = (): ContactLevel => {
        switch (levelKind) {
            case "account":
                return { kind: "account" };
            case "division":
                return { kind: "division", id: levelId };
            case "group":
                return { kind: "group", id: levelId };
            case "site":
                return { kind: "site", id: levelId };
        }
    };

    const save = useMutation({
        mutationFn: async () => {
            const level = currentLevel();
            if (level.kind !== "account" && !level.id) throw new Error("PICK_TARGET");

            let v2Id = contact?.id ?? null;
            let methods = contact?.methods ?? [];

            if (!isEdit) {
                const created = await api.post<{ contact: { id: string } }>(`/api/customers/${customerId}/contacts`, {
                    firstName: firstName.trim(),
                    lastName: lastName.trim() || null,
                    email: email.trim() || null,
                    phone: phone.trim() || null,
                    roleTitle: roleTitle.trim() || null,
                });
                // The legacy CRUD mirrors into the contacts hierarchy in the
                // same transaction; look the mirror up to get the v2 contact
                // + method ids the routing layer works with.
                const fresh = await api.get<{ contacts: RoutingContact[] }>(
                    `/api/customers/${customerId}/contact-methods`,
                );
                const mirror = fresh.contacts.find((c) => c.sourceContactId === created.contact.id);
                if (!mirror) throw new Error("MIRROR_NOT_FOUND");
                v2Id = mirror.id;
                methods = mirror.methods;
            } else if (!identityLocked && contact?.sourceContactId) {
                await api.patch(`/api/customers/${customerId}/contacts/${contact.sourceContactId}`, {
                    firstName: firstName.trim(),
                    lastName: lastName.trim() || null,
                    email: email.trim() || null,
                    phone: phone.trim() || null,
                    roleTitle: roleTitle.trim() || null,
                });
                const fresh = await api.get<{ contacts: RoutingContact[] }>(
                    `/api/customers/${customerId}/contact-methods`,
                );
                const mirror = fresh.contacts.find((c) => c.id === contact.id);
                if (mirror) methods = mirror.methods;
            }
            if (!v2Id) throw new Error("MIRROR_NOT_FOUND");

            // Scope + affiliation (one PATCH sets the whole triple).
            const levelChanged =
                level.kind !== initialLevel.kind ||
                ("id" in level ? level.id : "") !== ("id" in initialLevel ? initialLevel.id : "");
            if (!isEdit || levelChanged || worksFor !== (contact?.worksFor ?? "account")) {
                const scopeBody: Record<string, unknown> = { worksFor };
                scopeBody.siteId = level.kind === "site" ? level.id : null;
                scopeBody.locationGroupId = level.kind === "group" ? level.id : null;
                scopeBody.divisionId = level.kind === "division" ? level.id : null;
                await api.patch(`/api/contacts/${v2Id}/scope`, scopeBody);
            }

            // "Who gets X?" — attach/detach the person's best method on the
            // rule at THEIR level. Detaching removes the purpose wherever it
            // was attached; attachments made at other levels earlier stay
            // where the office put them.
            const bestMethod =
                methods.find((m) => m.kind === "email" && m.isPrimary) ??
                methods.find((m) => m.kind === "email") ??
                methods.find((m) => m.kind === "phone");
            const wanted = Array.from(purposes).filter((p) => !initialPurposes.has(p));
            const dropped = Array.from(initialPurposes).filter((p) => !purposes.has(p));
            const declined: string[] = [];

            if (wanted.length > 0 && !bestMethod) throw new Error("NO_METHOD");
            for (const purposeKey of wanted) {
                const ruleId = await ensureRuleId({ customerId, level, purposeKey, rules });
                const result = await attachRecipientGuarded({ ruleId, contactMethodId: bestMethod!.id });
                if (result === "declined") declined.push(purposeKey);
            }
            if (dropped.length > 0) {
                const methodIds = new Set(methods.map((m) => m.id));
                for (const r of rules) {
                    if (!dropped.includes(r.purpose_key)) continue;
                    for (const rec of r.recipients) {
                        if (methodIds.has(rec.contact_method_id)) {
                            await api.delete(`/api/routing-rules/${r.id}/recipients/${rec.contact_method_id}`);
                        }
                    }
                }
            }
            return { declined };
        },
        onSuccess: ({ declined }) => {
            qc.invalidateQueries({ queryKey: ["customer", customerId, "routing"] });
            qc.invalidateQueries({ queryKey: ["customer", customerId, "contacts"] });
            if (declined.length > 0) {
                toast.warning(
                    `Saved, but skipped ${declined.map((p) => PURPOSE_META[p]?.question ?? p).join(", ")} — billing guardrail declined`,
                );
            } else {
                toast.success(isEdit ? "Person updated" : "Person added");
            }
            onClose();
        },
        onError: (e) => {
            if (e instanceof Error && e.message === "PICK_TARGET") {
                toast.error("Pick which division, group, or site they belong to");
            } else if (e instanceof Error && e.message === "NO_METHOD") {
                toast.error("Add an email or phone so documents have somewhere to go");
            } else {
                toast.error(isEdit ? "Couldn't update the person" : "Couldn't add the person");
            }
        },
    });

    const filteredSites = sites.filter(
        (s) => !siteFilter.trim() || (s.name ?? "").toLowerCase().includes(siteFilter.trim().toLowerCase()),
    );
    const billingRisk = worksFor === "end_customer" && (purposes.has("send_invoices") || purposes.has("send_quotes"));

    return (
        <Sheet open onOpenChange={(o) => !o && onClose()}>
            <SheetContent className="w-full overflow-y-auto sm:w-[480px]">
                <SheetHeader className="pr-12">
                    <SheetTitle>{isEdit ? "Edit person" : "Add person"}</SheetTitle>
                    <SheetDescription>
                        Say who they are, where they sit, and what they should get — routing follows automatically.
                    </SheetDescription>
                </SheetHeader>
                <form
                    className="space-y-4 p-4"
                    onSubmit={(e) => {
                        e.preventDefault();
                        if (!isEdit && !firstName.trim()) {
                            toast.error("First name is required");
                            return;
                        }
                        save.mutate();
                    }}
                >
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-medium">First name *</label>
                            <Input
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                autoFocus={!isEdit}
                                disabled={identityLocked}
                            />
                        </div>
                        <div>
                            <label className="text-xs font-medium">Last name</label>
                            <Input
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                                disabled={identityLocked}
                            />
                        </div>
                    </div>
                    {isEdit ? (
                        <ContactAddressList
                            customerId={customerId}
                            contactId={contact!.id}
                            methods={contact!.methods}
                        />
                    ) : (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs font-medium">Email</label>
                                <Input value={email} onChange={(e) => setEmail(e.target.value)} />
                            </div>
                            <div>
                                <label className="text-xs font-medium">Phone</label>
                                <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
                            </div>
                        </div>
                    )}
                    <div>
                        <label className="text-xs font-medium">Role / title</label>
                        <Input
                            value={roleTitle}
                            onChange={(e) => setRoleTitle(e.target.value)}
                            placeholder="e.g. Facilities manager"
                            disabled={identityLocked}
                        />
                    </div>
                    {identityLocked && (
                        <p className="text-[11px] text-foreground/60">
                            Name and role are imported and stay as imported. You can still add or remove addresses
                            above, and set where they sit and what they receive.
                        </p>
                    )}

                    {/* Where do they sit? */}
                    <div className="space-y-2">
                        <label className="text-xs font-semibold">Where do they sit?</label>
                        <Select
                            value={levelKind}
                            onChange={(e) => {
                                const kind = e.target.value as ContactLevel["kind"];
                                setLevelKind(kind);
                                setLevelId("");
                                // Reset affiliation on level change; picking an
                                // end-customer group below flips it to
                                // end_customer as the sensible default.
                                if (kind === "group") setWorksFor("account");
                            }}
                        >
                            <option value="account">Whole account (default)</option>
                            {divisions.length > 0 && <option value="division">A division</option>}
                            {groups.length > 0 && <option value="group">A group / brand</option>}
                            <option value="site">One specific site</option>
                        </Select>
                        {levelKind === "division" && (
                            <Select
                                value={levelId}
                                onChange={(e) => setLevelId(e.target.value)}
                                aria-label="Pick division"
                            >
                                <option value="">Pick the division…</option>
                                {divisions.map((d) => (
                                    <option key={d.id} value={d.id}>
                                        {d.name}
                                    </option>
                                ))}
                            </Select>
                        )}
                        {levelKind === "group" && (
                            <Select
                                value={levelId}
                                onChange={(e) => {
                                    setLevelId(e.target.value);
                                    const g = groups.find((x) => x.id === e.target.value);
                                    if (g?.is_end_customer) setWorksFor("end_customer");
                                }}
                                aria-label="Pick group"
                            >
                                <option value="">Pick the group…</option>
                                {groups.map((g) => (
                                    <option key={g.id} value={g.id}>
                                        {g.name}
                                        {g.is_end_customer ? " (their customer)" : ""}
                                    </option>
                                ))}
                            </Select>
                        )}
                        {levelKind === "site" && (
                            <div className="space-y-1.5">
                                <Input
                                    value={siteFilter}
                                    onChange={(e) => setSiteFilter(e.target.value)}
                                    placeholder="Filter sites…"
                                    aria-label="Filter sites"
                                />
                                <Select
                                    value={levelId}
                                    onChange={(e) => setLevelId(e.target.value)}
                                    aria-label="Pick site"
                                >
                                    <option value="">Pick the site…</option>
                                    {filteredSites.slice(0, 300).map((s) => (
                                        <option key={s.id} value={s.id}>
                                            {s.name ?? s.id.slice(0, 8)}
                                            {s.city ? ` · ${s.city}` : ""}
                                        </option>
                                    ))}
                                </Select>
                            </div>
                        )}
                    </div>

                    {/* Who do they work for? */}
                    <div className="space-y-1.5">
                        <label className="text-xs font-semibold">Who do they work for?</label>
                        <div className="flex flex-col gap-1.5 text-sm">
                            <label className="flex items-center gap-2">
                                <input
                                    type="radio"
                                    name="worksFor"
                                    checked={worksFor === "account"}
                                    onChange={() => setWorksFor("account")}
                                />
                                {customerName} (the account that pays)
                            </label>
                            <label className="flex items-center gap-2">
                                <input
                                    type="radio"
                                    name="worksFor"
                                    checked={worksFor === "end_customer"}
                                    onChange={() => setWorksFor("end_customer")}
                                />
                                Their customer — the brand at the site (never billed)
                            </label>
                        </div>
                    </div>

                    {/* Who gets what? */}
                    <div className="space-y-1.5">
                        <label className="text-xs font-semibold">What should they get?</label>
                        <div className="flex flex-col gap-1.5 text-sm">
                            {CORE_PURPOSES.map((p) => (
                                <label key={p} className="flex items-center gap-2">
                                    <Checkbox
                                        checked={purposes.has(p)}
                                        onCheckedChange={(v) =>
                                            setPurposes((prev) => {
                                                const next = new Set(prev);
                                                if (v === true) next.add(p);
                                                else next.delete(p);
                                                return next;
                                            })
                                        }
                                        aria-label={PURPOSE_META[p].question}
                                    />
                                    {PURPOSE_META[p].question}
                                </label>
                            ))}
                        </div>
                        <p className="text-[11px] text-foreground/60">
                            Applies at the level they sit — everything below inherits it automatically.
                        </p>
                    </div>

                    {billingRisk && (
                        <div className="rounded-lg border border-orange-300 bg-orange-50 p-2.5 text-xs text-orange-900 dark:border-orange-500/40 dark:bg-orange-500/10 dark:text-orange-200">
                            <b>Heads up:</b> this person works for the end customer. {customerName} pays for these sites
                            — you&apos;ll be asked to confirm before they receive invoices or quotes.
                        </div>
                    )}

                    <div className="flex justify-end gap-2 pt-1">
                        <Button type="button" variant="outline" size="sm" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" size="sm" disabled={save.isPending}>
                            {save.isPending ? "Saving…" : isEdit ? "Save changes" : "Add person"}
                        </Button>
                    </div>
                </form>
            </SheetContent>
        </Sheet>
    );
}

// ────────────────────────────────────────────────────────────
// Removing people — the plain-English half
// ────────────────────────────────────────────────────────────

/**
 * The sentence shown before a bulk removal (#1303). Says out loud that
 * imported people stay gone, because that is the surprising part: everything
 * else about ServiceTitan data comes back on the next sync, and the office
 * has been told so repeatedly.
 */
export function removalConfirmMessage(people: RoutingContact[]): string {
    return buildRemovalMessage({
        count: people.length,
        importedCount: people.filter(isImportedContact).length,
        singleName: people.length === 1 ? contactDisplayName(people[0]!) : undefined,
    });
}

/** True when any part of this person came out of the ServiceTitan import. */
export function isImportedContact(c: RoutingContact): boolean {
    return c.source === "st_import" || c.methods.some((m) => m.fromServiceTitan);
}

export function contactDisplayName(c: RoutingContact): string {
    return `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim() || "this contact";
}

// ────────────────────────────────────────────────────────────
// People list
// ────────────────────────────────────────────────────────────

function levelLabel(
    c: RoutingContact,
    divisions: RoutingDivision[],
    groups: RoutingGroup[],
    sites: RoutingSite[],
): string {
    const level = contactLevel(c);
    switch (level.kind) {
        case "site": {
            const s = sites.find((x) => x.id === level.id);
            return `SITE · ${(s?.name ?? "site").toUpperCase()}`;
        }
        case "group": {
            const g = groups.find((x) => x.id === level.id);
            return `GROUP · ${(g?.name ?? "group").toUpperCase()}`;
        }
        case "division": {
            const d = divisions.find((x) => x.id === level.id);
            return `DIVISION · ${(d?.name ?? "division").toUpperCase()}`;
        }
        default:
            return "ACCOUNT";
    }
}

/**
 * The people on the account, and — behind an explicit Edit toggle — the way
 * to clear several of them at once (#1303).
 *
 * The checkboxes are deliberately not always on. This list is read far more
 * often than it is edited, and a permanent column of checkboxes next to a
 * "Remove" button invites the accident. Edit is the seatbelt: you say you are
 * editing, then you select, then you confirm.
 */
export function PeopleCard({
    customerName,
    contacts,
    divisions,
    groups,
    sites,
    rules,
    onAdd,
    onEdit,
    editing = false,
    onToggleEditing,
    selectedIds,
    onToggleSelected,
    onSelectAll,
    onRemoveSelected,
    removing = false,
}: {
    customerName: string;
    contacts: RoutingContact[];
    divisions: RoutingDivision[];
    groups: RoutingGroup[];
    sites: RoutingSite[];
    rules: RoutingRule[];
    onAdd?: () => void;
    onEdit?: (c: RoutingContact) => void;
    /** Selection mode — checkboxes only exist while this is on. */
    editing?: boolean;
    onToggleEditing?: (next: boolean) => void;
    selectedIds?: Set<string>;
    onToggleSelected?: (contactId: string) => void;
    onSelectAll?: (all: boolean) => void;
    onRemoveSelected?: () => void;
    removing?: boolean;
}) {
    const selected = selectedIds ?? new Set<string>();
    const selectable = editing && !!onToggleSelected;
    const allSelected = contacts.length > 0 && contacts.every((c) => selected.has(c.id));
    const selectedCount = contacts.filter((c) => selected.has(c.id)).length;

    return (
        <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-3">
                <CardTitle className="text-xs font-bold uppercase tracking-widest text-foreground/60">
                    People on this account
                </CardTitle>
                <div className="flex items-center gap-2">
                    {onToggleEditing && contacts.length > 0 && (
                        <Button
                            size="sm"
                            variant={editing ? "secondary" : "ghost"}
                            data-testid="people-edit-toggle"
                            aria-pressed={editing}
                            onClick={() => onToggleEditing(!editing)}
                        >
                            <Pencil className="mr-1.5 h-3.5 w-3.5" /> {editing ? "Done" : "Edit"}
                        </Button>
                    )}
                    {onAdd && (
                        <Button size="sm" variant="outline" onClick={onAdd}>
                            <UserPlus className="mr-1.5 h-3.5 w-3.5" /> Add person
                        </Button>
                    )}
                </div>
            </CardHeader>
            <CardContent className="space-y-1">
                {selectable && (
                    <div
                        data-testid="people-select-bar"
                        className="mb-1 flex flex-wrap items-center gap-3 rounded-lg border border-dashed bg-muted/40 px-2.5 py-2 text-xs"
                    >
                        <label className="flex cursor-pointer items-center gap-2 font-medium">
                            <Checkbox
                                checked={allSelected}
                                aria-label="Select everyone"
                                onCheckedChange={(v) => onSelectAll?.(v === true)}
                            />
                            Select all {contacts.length}
                        </label>
                        <span className="text-foreground/70">
                            {selectedCount === 0 ? "Tick the people to remove" : `${selectedCount} selected`}
                        </span>
                        <Button
                            size="sm"
                            variant="destructive"
                            className="ml-auto h-7"
                            data-testid="people-remove-selected"
                            disabled={selectedCount === 0 || removing}
                            onClick={() => onRemoveSelected?.()}
                        >
                            {removing ? (
                                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
                            ) : (
                                <Trash2 className="mr-1.5 h-3.5 w-3.5" />
                            )}
                            Remove {selectedCount > 0 ? selectedCount : ""}
                        </Button>
                    </div>
                )}
                {contacts.length === 0 && (
                    <p className="py-2 text-sm text-foreground/70">
                        No people yet — add the first one and say what they should receive.
                    </p>
                )}
                {contacts.map((c) => {
                    const stale = isContactStale(c.lastVerifiedAt);
                    const purposeSet = attachedPurposes(c, rules);
                    const name = `${c.firstName ?? ""} ${c.lastName ?? ""}`.trim() || "—";
                    const imported = isImportedContact(c);
                    return (
                        <div
                            key={c.id}
                            data-testid={`person-row-${c.id}`}
                            className={cn(
                                "flex items-start gap-3 border-b border-dashed border-border/60 py-2.5 last:border-none",
                                selectable && selected.has(c.id) && "bg-destructive/5",
                            )}
                        >
                            {selectable && (
                                <Checkbox
                                    className="mt-2"
                                    data-testid={`person-select-${c.id}`}
                                    checked={selected.has(c.id)}
                                    aria-label={`Select ${name}`}
                                    onCheckedChange={() => onToggleSelected?.(c.id)}
                                />
                            )}
                            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                                {(c.firstName?.[0] ?? "?").toUpperCase()}
                                {(c.lastName?.[0] ?? "").toUpperCase()}
                            </div>
                            <div className="min-w-0 flex-1">
                                <div className="flex flex-wrap items-center gap-1.5 text-sm font-semibold">
                                    {name}
                                    <WorksForBadge worksFor={c.worksFor} customerName={customerName} />
                                    <LevelBadge label={levelLabel(c, divisions, groups, sites)} />
                                    {imported && (
                                        <span className="rounded border px-1.5 py-px text-[9px] font-bold uppercase tracking-wider text-foreground/55">
                                            Imported
                                        </span>
                                    )}
                                    <span
                                        className={cn(
                                            "text-[10px] font-bold",
                                            stale
                                                ? "text-amber-600 dark:text-amber-400"
                                                : "text-emerald-700 dark:text-emerald-400",
                                        )}
                                        title={c.lastVerifiedByName ? `by ${c.lastVerifiedByName}` : undefined}
                                    >
                                        {stale ? "⚠ " : "✓ "}
                                        {formatVerifiedLabel(c.lastVerifiedAt)}
                                    </span>
                                </div>
                                <div className="truncate text-[11.5px] text-foreground/70">
                                    {[...c.methods.map((m) => m.value), c.roleTitle ?? undefined]
                                        .filter(Boolean)
                                        .join(" · ") || "no contact methods"}
                                </div>
                                {purposeSet.size > 0 && (
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {Array.from(purposeSet).map((p) => (
                                            <span
                                                key={p}
                                                className="rounded border border-amber-300/70 bg-amber-50 px-1.5 py-px text-[9px] font-extrabold tracking-wider text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300"
                                            >
                                                {purposeChipLabel(p)}
                                            </span>
                                        ))}
                                    </div>
                                )}
                            </div>
                            {onEdit && !selectable && (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-2"
                                    onClick={() => onEdit(c)}
                                    aria-label={`Edit ${name}`}
                                >
                                    <Pencil className="h-3.5 w-3.5" />
                                </Button>
                            )}
                        </div>
                    );
                })}
                <div className="mt-2 rounded-lg border border-orange-200 bg-orange-50/70 p-2.5 text-xs text-orange-900 dark:border-orange-500/30 dark:bg-orange-500/5 dark:text-orange-200">
                    <b>Billing guardrail:</b> people marked <b>THEIR CUSTOMER</b> can&apos;t be added to invoices or
                    quotes without an explicit override — {customerName} pays for these sites.
                </div>
            </CardContent>
        </Card>
    );
}

/**
 * The undo for a removed import (#1303).
 *
 * A bulk delete of imported data needs a way back, and the way back has to be
 * visible from the same screen that did the deleting — a restore that only
 * exists as an API call is not a restore anyone will find at 4pm on a Friday.
 * Each row is a tombstone in `contact_import_suppressions`; deleting it lets
 * the next sync bring the record back, which is what the copy says.
 */
function RemovedImportsCard({ customerId }: { customerId: string }) {
    const qc = useQueryClient();
    const [open, setOpen] = useState(false);

    const suppressionsQ = useQuery({
        queryKey: ["customer", customerId, "contact-suppressions"],
        queryFn: () =>
            api.get<{ suppressions: ImportSuppression[] }>(`/api/customers/${customerId}/contact-suppressions`),
    });
    const rows = suppressionsQ.data?.suppressions ?? [];

    const restore = useMutation({
        mutationFn: (id: string) => api.delete(`/api/customers/${customerId}/contact-suppressions/${id}`),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["customer", customerId, "contact-suppressions"] });
            toast.success("Restored — it comes back the next time the job system syncs (about 10 minutes).");
        },
        onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't restore that one"),
    });

    if (rows.length === 0) return null;

    return (
        <div className="rounded-lg border border-dashed bg-muted/30 px-3 py-2 text-xs" data-testid="removed-imports">
            <button
                type="button"
                className="flex w-full items-center gap-1.5 text-left font-medium text-foreground/75"
                onClick={() => setOpen((o) => !o)}
            >
                {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                {rows.length} imported {rows.length === 1 ? "address" : "addresses"} removed — kept out of the sync
            </button>
            {open && (
                <ul className="mt-2 space-y-1">
                    {rows.map((r) => (
                        <li key={r.id} className="flex items-center gap-2 rounded-md border bg-card px-2.5 py-1.5">
                            <span className="min-w-0 flex-1 truncate">
                                {r.value ?? r.stContactId}
                                {r.contactName ? <span className="text-foreground/60"> · {r.contactName}</span> : null}
                            </span>
                            <Button
                                type="button"
                                size="sm"
                                variant="ghost"
                                className="h-7 shrink-0 px-2 text-xs"
                                disabled={restore.isPending}
                                onClick={() => restore.mutate(r.id)}
                            >
                                <RotateCcw className="mr-1 h-3.5 w-3.5" /> Restore
                            </Button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}

// ────────────────────────────────────────────────────────────
// "How a send resolves" panel — driven by the REAL resolver
// ────────────────────────────────────────────────────────────

const SCOPE_STEP_LABEL: Record<string, string> = {
    site: "Site rule",
    group: "Group",
    division: "Division",
    customer: "Account",
};

export function ResolvePanel({ customerId, sites }: { customerId: string; sites: RoutingSite[] }) {
    const [siteId, setSiteId] = useState("");
    const [purpose, setPurpose] = useState("send_reports");
    const [filter, setFilter] = useState("");

    const resolutionQ = useQuery({
        queryKey: ["customer", customerId, "routing", "resolution", siteId, purpose],
        queryFn: () =>
            api.get<ResolutionResponse>(
                `/api/customers/${customerId}/routing-resolution?siteId=${encodeURIComponent(siteId)}&purpose=${encodeURIComponent(purpose)}`,
            ),
        enabled: !!siteId && !!purpose,
    });

    const filtered = sites.filter(
        (s) => !filter.trim() || (s.name ?? "").toLowerCase().includes(filter.trim().toLowerCase()),
    );
    const res = resolutionQ.data;
    const winners = res?.recipients.filter((r) => !r.always_cc) ?? [];
    const riders = res?.recipients.filter((r) => r.always_cc) ?? [];
    // The rung that decided the send. When the customer rule wins with only
    // always_cc recipients, the winner IS the customer rung (edge case).
    const winnerScope = winners[0]?.resolved_from.scope ?? (riders.length > 0 ? "customer" : null);

    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="text-xs font-bold uppercase tracking-widest text-foreground/60">
                    How a send resolves{" "}
                    <span className="font-medium normal-case tracking-normal text-muted-foreground">
                        · live example
                    </span>
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
                <Input
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Filter sites…"
                    aria-label="Filter resolution sites"
                />
                <Select value={siteId} onChange={(e) => setSiteId(e.target.value)} aria-label="Resolution site">
                    <option value="">Pick a site…</option>
                    {filtered.slice(0, 300).map((s) => (
                        <option key={s.id} value={s.id}>
                            {s.name ?? s.id.slice(0, 8)}
                            {s.city ? ` · ${s.city}` : ""}
                        </option>
                    ))}
                </Select>
                <Select value={purpose} onChange={(e) => setPurpose(e.target.value)} aria-label="Resolution purpose">
                    {CORE_PURPOSES.map((p) => (
                        <option key={p} value={p}>
                            {PURPOSE_META[p].question}
                        </option>
                    ))}
                </Select>

                {!siteId && (
                    <p className="text-xs text-foreground/60">Pick a site to see exactly who a send would reach.</p>
                )}
                {siteId && resolutionQ.isLoading && <Skeleton className="h-24 w-full" />}
                {siteId && res && (
                    <div className="space-y-1 rounded-lg bg-muted/40 p-3 text-[12.5px] leading-relaxed">
                        {res.ladder.map((rung, i) => {
                            // Everything shown here comes straight from the
                            // resolver's resolved_from — never re-derived.
                            const fromHere = res.recipients.filter((r) => r.resolved_from.scope === rung.scope);
                            const matched = rung.scope === winnerScope;
                            const winnerIdx = res.ladder.findIndex((l) => l.scope === winnerScope);
                            const beforeWinner = winnerIdx === -1 || i < winnerIdx;
                            return (
                                <div key={rung.scope} data-testid={`ladder-${rung.scope}`}>
                                    {i + 1} · {SCOPE_STEP_LABEL[rung.scope] ?? rung.scope}
                                    {rung.scope !== "site" && rung.scope !== "customer" ? ` (${rung.label})` : ""}:{" "}
                                    {matched ? (
                                        <b className="text-amber-800 dark:text-amber-300">
                                            {fromHere.map((w) => w.value).join(", ")}
                                        </b>
                                    ) : beforeWinner ? (
                                        <span className="text-foreground/60">no rule → skip</span>
                                    ) : rung.scope === "customer" && fromHere.length > 0 ? (
                                        <b className="text-primary">
                                            always-CC {fromHere.map((r) => r.value).join(", ")}
                                        </b>
                                    ) : (
                                        <span className="text-foreground/60">replaced by the winning rule ↑</span>
                                    )}
                                </div>
                            );
                        })}
                        <div className="pt-1 text-foreground/70">
                            {res.recipients.length === 0 ? (
                                <>
                                    No routing set anywhere on the ladder — the office picks recipients at send time, as
                                    today. Nothing is blocked.
                                </>
                            ) : (
                                <>
                                    → one email, {res.recipients.length} recipient
                                    {res.recipients.length === 1 ? "" : "s"}.
                                </>
                            )}
                        </div>
                    </div>
                )}

                <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-2.5 text-xs text-foreground/80 dark:border-amber-500/30 dark:bg-amber-500/5">
                    <b className="text-primary">Rule of thumb:</b> set people at the highest level that&apos;s true.
                    Only touch a site when that site is genuinely different.
                </div>
            </CardContent>
        </Card>
    );
}

// ────────────────────────────────────────────────────────────
// Main tab
// ────────────────────────────────────────────────────────────

export function ContactsRoutingTab({ customerId, customerName }: { customerId: string; customerName: string }) {
    const qc = useQueryClient();
    const [drawer, setDrawer] = useState<{ mode: "add" } | { mode: "edit"; contact: RoutingContact } | null>(null);
    const [assistOpen, setAssistOpen] = useState(false);
    const [addDivisionOpen, setAddDivisionOpen] = useState(false);
    const [addGroupOpen, setAddGroupOpen] = useState(false);
    const [assistSnoozedNow, setAssistSnoozedNow] = useState(() => assistSnoozed(customerId));
    // Removing people (#1303) — checkboxes exist only while editing is on.
    const [editingPeople, setEditingPeople] = useState(false);
    const [selectedPeople, setSelectedPeople] = useState<Set<string>>(new Set());

    const divisionsQ = useQuery({
        queryKey: ["customer", customerId, "routing", "divisions"],
        queryFn: () => api.get<{ divisions: RoutingDivision[] }>(`/api/customers/${customerId}/divisions`),
    });
    const groupsQ = useQuery({
        queryKey: ["customer", customerId, "routing", "groups"],
        queryFn: () => api.get<{ locationGroups: RoutingGroup[] }>(`/api/customers/${customerId}/location-groups`),
    });
    const rulesQ = useQuery({
        queryKey: ["customer", customerId, "routing", "rules"],
        queryFn: () => api.get<{ routingRules: RoutingRule[] }>(`/api/customers/${customerId}/routing-rules`),
    });
    const sitesQ = useQuery({
        queryKey: ["customer", customerId, "routing", "sites"],
        queryFn: () => api.get<{ sites: RoutingSite[] }>(`/api/customers/${customerId}/routing-sites`),
    });
    const contactsQ = useQuery({
        queryKey: ["customer", customerId, "contacts", "methods"],
        queryFn: () => api.get<{ contacts: RoutingContact[] }>(`/api/customers/${customerId}/contact-methods`),
    });

    const divisions = divisionsQ.data?.divisions ?? [];
    const groups = groupsQ.data?.locationGroups ?? [];
    const rules = rulesQ.data?.routingRules ?? [];
    const sites = sitesQ.data?.sites ?? [];
    const contacts = contactsQ.data?.contacts ?? [];

    const loading = divisionsQ.isLoading || groupsQ.isLoading || rulesQ.isLoading || sitesQ.isLoading;

    const removeRecipient = useMutation({
        mutationFn: ({ ruleId, contactMethodId }: { ruleId: string; contactMethodId: string; value: string }) =>
            api.delete(`/api/routing-rules/${ruleId}/recipients/${contactMethodId}`),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["customer", customerId, "routing"] });
            toast.success("Routing removed — that address is no longer on this level");
        },
        onError: () => toast.error("Couldn't remove the routing"),
    });

    const onRemoveRecipient = async (ruleId: string, contactMethodId: string, value: string) => {
        if (
            await confirmDialog({
                message: `Stop sending to ${value} at this level? Sends fall back to the level above.`,
                confirmLabel: "Remove",
            })
        ) {
            removeRecipient.mutate({ ruleId, contactMethodId, value });
        }
    };

    const removePeople = useMutation({
        mutationFn: (contactIds: string[]) => removeContactsInBatches(customerId, contactIds),
        onSuccess: (res) => {
            // Everything downstream of a person can change: the tree, the
            // resolution panel, and the restore list all re-read.
            qc.invalidateQueries({ queryKey: ["customer", customerId, "routing"] });
            qc.invalidateQueries({ queryKey: ["customer", customerId, "contacts"] });
            qc.invalidateQueries({ queryKey: ["customer", customerId, "contact-suppressions"] });
            // Only clear what actually went — a blocked person stays ticked so
            // the office can see which ones still need dealing with.
            const gone = new Set(res.results.filter((r) => r.status !== "blocked").map((r) => r.contactId));
            setSelectedPeople((prev) => new Set([...prev].filter((id) => !gone.has(id))));
            const { tone, text } = summarizeRemoval(res);
            if (tone === "success") toast.success(text);
            else if (tone === "warning") toast.warning(text, { duration: 10000 });
            else toast.error(text, { duration: 10000 });
        },
        onError: (e) => toast.error(e instanceof ApiError ? e.message : "Couldn't remove those people"),
    });

    const onRemoveSelected = async () => {
        const people = contacts.filter((c) => selectedPeople.has(c.id));
        if (people.length === 0) return;
        const ok = await confirmDialog({
            title: people.length === 1 ? "Remove this person?" : `Remove ${people.length} people?`,
            message: removalConfirmMessage(people),
            confirmLabel: people.length === 1 ? "Remove" : `Remove ${people.length}`,
            destructive: true,
            focusCancel: true,
        });
        if (ok) removePeople.mutate(people.map((c) => c.id));
    };

    const suggestions = useMemo(() => suggestGroups(sites, groups), [sites, groups]);
    const suggestedSiteCount = suggestions.reduce((n, s) => n + s.siteIds.length, 0);
    const summary = useMemo(() => coverageSummary(rules, contacts), [rules, contacts]);
    const ungroupedCount = sites.filter((s) => s.location_group_id === null).length;

    if (loading) {
        return (
            <div className="space-y-4">
                <Skeleton className="h-14 w-full rounded-xl" />
                <Skeleton className="h-64 w-full rounded-xl" />
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Lifecycle audit 10/29: what bounced, and one tap to send it again once the address is fixed. */}
            <BouncedSendsCard customerId={customerId} />
            {/* Coverage banner — a progress card, never a gate */}
            <Card>
                <CardContent className="flex flex-wrap items-center gap-x-5 gap-y-1.5 py-3 text-xs font-semibold">
                    {summary.uncoveredPurposes.length === 0 ? (
                        <span className="text-emerald-700 dark:text-emerald-400" data-testid="coverage-ok">
                            ✓ Every purpose has a recipient — nothing falls back to a guess
                        </span>
                    ) : (
                        <span className="text-foreground/70" data-testid="coverage-gap">
                            {CORE_PURPOSES.length - summary.uncoveredPurposes.length} of {CORE_PURPOSES.length} purposes
                            have an account default — the rest ask the office at send time (nothing is blocked)
                        </span>
                    )}
                    {(summary.siteExceptionCount > 0 || summary.staleContactCount > 0) && (
                        <span className="flex items-center gap-1 text-amber-700 dark:text-amber-400">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            {[
                                summary.siteExceptionCount > 0
                                    ? `${summary.siteExceptionCount} site exception${summary.siteExceptionCount === 1 ? "" : "s"}`
                                    : null,
                                summary.staleContactCount > 0
                                    ? `${summary.staleContactCount} contact${summary.staleContactCount === 1 ? "" : "s"} unverified > 12 months`
                                    : null,
                            ]
                                .filter(Boolean)
                                .join(" · ")}
                        </span>
                    )}
                    <span className="ml-auto">
                        <Button size="sm" onClick={() => setDrawer({ mode: "add" })}>
                            <UserPlus className="mr-1.5 h-3.5 w-3.5" /> Add person
                        </Button>
                    </span>
                </CardContent>
            </Card>

            {/* How to read this */}
            <p className="px-1 text-[11.5px] text-shell-foreground/80">
                <b className="text-shell-foreground">How to read this:</b> anything not set at a level inherits from the
                level above. Only exceptions are shown — a green pill means &quot;nothing special here.&quot;
            </p>

            {/* Auto-group assist — snoozeable, purely a helper */}
            {suggestions.length > 0 && !assistSnoozedNow && (
                <div
                    data-testid="auto-group-assist"
                    className="flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-primary/40 bg-card px-4 py-2.5 text-xs"
                >
                    <Sparkles className="h-3.5 w-3.5 text-primary" />
                    <span>
                        <b>
                            {ungroupedCount} ungrouped site{ungroupedCount === 1 ? "" : "s"}
                        </b>{" "}
                        — {suggestedSiteCount} match name patterns (
                        {suggestions
                            .slice(0, 3)
                            .map((s) => `${s.name} ×${s.siteIds.length}`)
                            .join(", ")}
                        {suggestions.length > 3 ? ", …" : ""}). <b>Group them?</b> You confirm each batch.
                    </span>
                    <span className="ml-auto flex items-center gap-2">
                        <Button size="sm" onClick={() => setAssistOpen(true)}>
                            Review suggestions
                        </Button>
                        <Button
                            size="sm"
                            variant="ghost"
                            aria-label="Snooze grouping suggestions"
                            onClick={() => {
                                snoozeAssist(customerId);
                                setAssistSnoozedNow(true);
                            }}
                        >
                            <X className="h-3.5 w-3.5" />
                        </Button>
                    </span>
                </div>
            )}

            {/* Hierarchy tree */}
            <HierarchyTree
                customerName={customerName}
                divisions={divisions}
                groups={groups}
                sites={sites}
                rules={rules}
                onRemoveRecipient={onRemoveRecipient}
                onAddDivision={() => setAddDivisionOpen(true)}
                onAddGroup={() => setAddGroupOpen(true)}
            />

            {/* People + resolution panel */}
            <div className="grid items-start gap-4 lg:grid-cols-[1fr_360px]">
                <div className="space-y-3">
                    <PeopleCard
                        customerName={customerName}
                        contacts={contacts}
                        divisions={divisions}
                        groups={groups}
                        sites={sites}
                        rules={rules}
                        onAdd={() => setDrawer({ mode: "add" })}
                        onEdit={(c) => setDrawer({ mode: "edit", contact: c })}
                        editing={editingPeople}
                        onToggleEditing={(next) => {
                            setEditingPeople(next);
                            if (!next) setSelectedPeople(new Set());
                        }}
                        selectedIds={selectedPeople}
                        onToggleSelected={(id) =>
                            setSelectedPeople((prev) => {
                                const next = new Set(prev);
                                if (next.has(id)) next.delete(id);
                                else next.add(id);
                                return next;
                            })
                        }
                        onSelectAll={(all) => setSelectedPeople(all ? new Set(contacts.map((c) => c.id)) : new Set())}
                        onRemoveSelected={() => void onRemoveSelected()}
                        removing={removePeople.isPending}
                    />
                    <RemovedImportsCard customerId={customerId} />
                </div>
                <ResolvePanel customerId={customerId} sites={sites} />
            </div>

            <p className="text-center text-xs text-shell-foreground/80">
                Anything not set here follows the level above it.{" "}
                <b className="text-shell-foreground">Only exceptions are ever shown.</b>
            </p>

            {drawer && (
                <PersonDrawer
                    customerId={customerId}
                    customerName={customerName}
                    divisions={divisions}
                    groups={groups}
                    sites={sites}
                    rules={rules}
                    contact={drawer.mode === "edit" ? drawer.contact : undefined}
                    onClose={() => setDrawer(null)}
                />
            )}
            {assistOpen && (
                <AutoGroupSuggestionsDialog
                    customerId={customerId}
                    suggestions={suggestions}
                    onClose={() => setAssistOpen(false)}
                    onApplied={() => qc.invalidateQueries({ queryKey: ["customer", customerId, "routing"] })}
                />
            )}
            {addDivisionOpen && <AddDivisionDialog customerId={customerId} onClose={() => setAddDivisionOpen(false)} />}
            {addGroupOpen && (
                <AddGroupDialog customerId={customerId} divisions={divisions} onClose={() => setAddGroupOpen(false)} />
            )}
        </div>
    );
}
